package com.dotdrives.web.pages.updates

import com.dotdrives.controller.UpdatesController
import com.dotdrives.controller.UpdatesUiState
import com.dotdrives.controller.canTransitionReport
import com.dotdrives.design.DotAvatarPalette
import com.dotdrives.model.ReportStatus
import com.dotdrives.model.ReportUpdate
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotEmptyState
import com.dotdrives.web.ui.DotPill
import com.dotdrives.web.ui.DotSkeleton
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.textarea
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType
import kotlin.time.Clock

external interface ThreadPanelProps : Props {
    var state: UpdatesUiState
    var controller: UpdatesController
    var currentUserId: String
    var isStaff: Boolean
    var onShare: () -> Unit
}

val ThreadPanel = FC<ThreadPanelProps> { props ->
    val state = props.state
    val report = state.selectedReport

    div {
        className = ClassName("updates-thread-pane")

        when {
            state.selectedReportId == null -> ThreadEmptySelection()
            state.threadLoading && report == null -> ThreadSkeleton()
            state.threadError != null && report == null -> {
                div {
                    className = ClassName("updates-thread-body")
                    DotBanner {
                        message = state.threadError ?: "Something went wrong"
                        onRetry = props.controller::retry
                    }
                }
            }
            report != null -> {
                val statusStyle = reportStatusStyle(report.status)
                val canTransition = canTransitionReport(report, props.currentUserId, props.isStaff)

                div {
                    className = ClassName("updates-thread-head")
                    div {
                        div { className = ClassName("updates-thread-title"); +report.title }
                        div {
                            className = ClassName("updates-thread-subtitle")
                            +listOfNotNull(
                                report.assigneeName?.let { "Field report by $it" },
                                report.targetName,
                            ).joinToString(" · ")
                        }
                        div {
                            style = js("({marginTop: '8px'})")
                            DotPill { text = statusStyle.label; tone = statusStyle.tone }
                        }
                    }
                    div {
                        className = ClassName("updates-thread-actions")
                        if (canTransition && report.status == ReportStatus.DRAFT) {
                            button {
                                type = ButtonType.buttonType
                                className = ClassName("updates-thread-action")
                                disabled = state.updatingStatus
                                onClick = { props.controller.submitForReview() }
                                +"Submit for review"
                            }
                        }
                        if (props.isStaff && report.status != ReportStatus.SHARED) {
                            button {
                                type = ButtonType.buttonType
                                className = ClassName("updates-thread-action updates-thread-action-share")
                                onClick = { props.onShare() }
                                +"Share with givers"
                            }
                        }
                    }
                }

                state.threadError?.let { error ->
                    div {
                        className = ClassName("updates-thread-body")
                        DotBanner {
                            message = error
                            onRetry = props.controller::retry
                            className = "updates-inline-banner"
                        }
                    }
                }

                div {
                    className = ClassName("updates-thread-body")
                    div {
                        className = ClassName("updates-thread-messages")
                        if (state.thread.isEmpty() && !state.threadLoading) {
                            DotEmptyState {
                                emoji = "✎"
                                title = "No updates posted yet"
                                body = "Write the first update below to start this thread."
                                primaryAction = null
                            }
                        } else {
                            state.thread.forEach { message ->
                                ThreadMessage { key = message.id.unsafeCast<Key>(); this.message = message }
                            }
                        }
                        Composer {
                            draftBody = state.draftBody
                            posting = state.posting
                            onChange = props.controller::updateDraft
                            onPost = props.controller::postUpdate
                        }
                    }
                }
            }
        }
    }
}

private external interface ThreadMessageProps : Props {
    var message: ReportUpdate
}

private val ThreadMessage = FC<ThreadMessageProps> { props ->
    val message = props.message
    val (bg, _) = DotAvatarPalette.tintFor(message.authorUserId ?: message.id)
    val avatarStyle = js("({})")
    avatarStyle.backgroundColor = bg

    div {
        className = ClassName("updates-message")
        div {
            className = ClassName("updates-message-head")
            div {
                className = ClassName("updates-message-avatar")
                style = avatarStyle
                +updateInitials(message.authorName)
            }
            div {
                div { className = ClassName("updates-message-author"); +(message.authorName ?: "Email intake") }
                div {
                    className = ClassName("updates-message-when")
                    +relativeTimeLabel(message.createdAt, Clock.System.now())
                }
            }
            if (message.viaEmail) {
                span {
                    className = ClassName("updates-message-via-email")
                    DotPill { text = "via email intake"; tone = com.dotdrives.design.DotTone.Info }
                }
            }
        }
        div { className = ClassName("updates-message-body"); +message.body }
    }
}

private external interface ComposerProps : Props {
    var draftBody: String
    var posting: Boolean
    var onChange: (String) -> Unit
    var onPost: () -> Unit
}

/** Posting into a thread has no self-scoping (`report_updates_insert` gates on
 *  `has_report_write(org_id)` alone, not `assignee_user_id`) -- any org member, field or staff,
 *  may reply into any report's thread, matching the design's mixed-role sample thread. */
private val Composer = FC<ComposerProps> { props ->
    div {
        className = ClassName("updates-composer")
        textarea {
            className = ClassName("updates-composer-input")
            value = props.draftBody
            placeholder = "Write an update… drop photos here"
            ariaLabel = "Write an update"
            onChange = { props.onChange(it.currentTarget.value) }
        }
        div {
            className = ClassName("updates-composer-row")
            button {
                type = ButtonType.buttonType
                className = ClassName("dot-button dot-button-primary")
                disabled = props.draftBody.isBlank() || props.posting
                onClick = { props.onPost() }
                +"Post"
            }
        }
    }
}

private val ThreadEmptySelection = FC<Props> {
    div {
        className = ClassName("updates-thread-empty-wrap")
        DotEmptyState {
            emoji = "📣"
            title = "Select an update"
            body = "Choose a report thread on the left to read it, or start a new one."
            primaryAction = null
        }
    }
}

private val ThreadSkeleton = FC<Props> {
    div {
        className = ClassName("updates-thread-body")
        div {
            style = js("({display:'grid', gap:'14px'})")
            repeat(3) { DotSkeleton { height = "100px" } }
        }
    }
}
